import React, { useCallback, useRef } from 'react'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import Button from 'components/Button'
import { Box, Flex } from 'components/Box'
import { Formik } from 'formik'
import * as yup from 'yup'
import TextareaAutoSize from 'react-textarea-autosize'
import Markdown from 'components/Markdown'

import * as markdown from 'components/SuggestionsInput/markdownSupport'

const placeholderText = 'Enter a description…'

const shareDescriptionSchema = yup.object({
  description: yup.string().trim(),
})

const LINE_HEIGHT = rem(20)
const sharedDescriptionStyles = css`
  font-family: ${({ theme }) => theme.fonts.body};
  line-height: ${LINE_HEIGHT};
  display: block;
  padding: 0;
  resize: none;
  border: none;
  appearance: none;
  background-color: transparent;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.text.dark.secondary};
  width: 100%;
`

const StyledTextareaAutoSize = styled(TextareaAutoSize)`
  ${sharedDescriptionStyles};
  &:active,
  &:focus {
    outline: 0;
  }
  ::placeholder {
    font-style: italic;
    color: ${({ theme }) => theme.colors.text.dark.tertiary};
  }
`

const DescriptionBoxContainer = styled.pre`
  ${sharedDescriptionStyles};
  word-break: break-all;
  word-break: break-word;
  white-space: pre-wrap;
  min-height: ${LINE_HEIGHT};
  min-width: ${LINE_HEIGHT};
  margin: 0;
  max-height: 256px;
  overflow: auto;

  @media (max-height: 862px) {
    max-height: 92px;
  }

  a {
    white-space: pre-wrap;
  }
`

const DescriptionBoxPlaceholder = styled.span`
  ${sharedDescriptionStyles};
  font-style: italic;
  opacity: 0.6;
`

/** Checks if the given markdown string is a whitespace only string
 * Markdown Blockquote is considered whitespace
 */
const isWhitespaceOnlyMd = (str: string) => {
  let original = str
  if (str.charAt(0) === '>') {
    original = str.substring(1)
  }
  return original.trimStart() === ''
}
export const DescriptionBox = ({ children, ...props }: DescriptionBoxProps) => {
  let content: JSX.Element

  if (!children) {
    content = (
      <DescriptionBoxPlaceholder>{placeholderText}</DescriptionBoxPlaceholder>
    )
  } else {
    const whitespaceOnlyTextNode = '       '
    content = (
      <Markdown>
        {isWhitespaceOnlyMd(children) ? whitespaceOnlyTextNode : children}
      </Markdown>
    )
  }

  return (
    <DescriptionBoxContainer
      {...props}
      data-testid="share-meta-sidebar-description">
      {content}
    </DescriptionBoxContainer>
  )
}

interface DescriptionBoxProps {
  children: string
  onClick?: () => void
}

const focusCursorAtEnd = (e: React.FocusEvent<HTMLTextAreaElement>) => {
  const prev = e.target.value
  e.target.value = ''
  e.target.value = prev
}

const actOnEnterKey = (
  action: () => void,
  event: React.KeyboardEvent<HTMLTextAreaElement>
) => {
  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
    action()
    event.preventDefault()
    return true
  }
  return false
}

const actOnEscKey = (
  action: () => void,
  event: React.KeyboardEvent<HTMLTextAreaElement>
) => {
  if (event.key === 'Escape') {
    action()
    return true
  }
  return false
}

const ShareDescriptionForm = ({
  onSubmit,
  onCancel,
  values: { description },
}: ShareDescriptionFormProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>()

  const handleFormikRender = useCallback(
    formikBag => {
      const {
        handleChange,
        handleSubmit,
        values,
        isSubmitting,
        setFieldValue,
      } = formikBag

      const handleTextAreaKeyDown = (
        event: React.KeyboardEvent<HTMLTextAreaElement>
      ) => {
        if (actOnEnterKey(handleSubmit, event)) return
        if (actOnEscKey(onCancel, event)) return

        const auxKey = event.metaKey || event.ctrlKey

        const markDownChange = markdown.getChangeByEventKey(event.key)
        if (auxKey && markDownChange && textAreaRef.current) {
          event.preventDefault()

          const {
            selectionStart,
            selectionEnd,
            setSelectionRange,
          } = textAreaRef.current
          const { description: value = '' }: typeof values = values

          const { text, ...selection } = markdown.toggleTextWithChange(
            value,
            markDownChange,
            selectionStart,
            selectionEnd
          )

          setFieldValue('description', text)

          window.requestAnimationFrame(() => {
            setSelectionRange(selection.selectionStart, selection.selectionEnd)
          })
        }
      }

      return (
        <React.Fragment>
          <Flex.Form flexDirection="column" onSubmit={handleSubmit}>
            <StyledTextareaAutoSize
              autoFocus
              onFocus={focusCursorAtEnd}
              maxRows={10}
              name="description"
              placeholder={placeholderText}
              onChange={handleChange}
              onKeyDown={handleTextAreaKeyDown}
              value={values.description || ''}
              data-testid="share-description-textarea"
            />

            <Flex justifyContent="flex-end" mt={4}>
              <Button
                small
                disabled={isSubmitting}
                onClick={onCancel}
                type="button">
                Cancel
              </Button>
              <Button
                small
                loading={isSubmitting}
                variant="primary"
                type="submit">
                Save
              </Button>
            </Flex>
          </Flex.Form>
        </React.Fragment>
      )
    },
    [onCancel]
  )

  return (
    <Formik
      initialValues={{ description }}
      validationSchema={shareDescriptionSchema}
      onSubmit={onSubmit}>
      {handleFormikRender}
    </Formik>
  )
}

interface ShareDescriptionFormProps {
  onSubmit: (values: { description?: string }) => void
  onCancel: () => void
  values: {
    description?: string
  }
}

const ShareDescription = ({
  description,
  editing,
  disableEditing,
  enableEditing,
  onSubmit,
  ...props
}: ShareDescriptionProps) => (
  <Box {...props}>
    {editing ? (
      <ShareDescriptionForm
        onSubmit={onSubmit}
        onCancel={disableEditing}
        values={{
          description,
        }}
      />
    ) : (
      <DescriptionBox onClick={enableEditing}>{description}</DescriptionBox>
    )}
  </Box>
)

interface ShareDescriptionProps {
  description: string
  editing: boolean
  disableEditing: () => void
  enableEditing: () => void
  onSubmit: (values: { description?: string }) => void
}

export default ShareDescription
